import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePermission from '../Hooks/usePermission';
import { MATERIOVIGILANCE_READ, DOCUMENT_VAULT_READ } from '../constants/permissions';

import Materiovigilance from './Compliance/Materiovigilance';
import Certificates from './Compliance/Certificates';

const Compliance = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const allTabs = useMemo(() => [
    {
      key: 'materiovigilance',
      label: t('compliance.tabs.materiovigilance', 'Materiovigilance'),
      icon: 'alert-triangle',
      path: '/compliance/materiovigilance',
      visible: hasPermission(MATERIOVIGILANCE_READ),
      component: Materiovigilance,
      matchPaths: [
        '/compliance/materiovigilance',
        '/compliance/materiovigilance/new/descending',
        '/compliance/materiovigilance/new/ascending',
      ],
    },
    {
      key: 'certificates',
      label: t('compliance.tabs.certificates', 'Certificates'),
      icon: 'award',
      path: '/compliance/certificates',
      visible: hasPermission(DOCUMENT_VAULT_READ),
      component: Certificates,
      matchPaths: ['/compliance/certificates'],
    },
  ], [t, hasPermission]);

  const visibleTabs = useMemo(() => allTabs.filter((tab) => tab.visible), [allTabs]);

  const activeTab = useMemo(() => {
    const path = location.pathname;
    const matchedByExtra = visibleTabs.find(
      (tab) => tab.matchPaths && tab.matchPaths.some((p) => path.startsWith(p))
    );
    if (matchedByExtra) return matchedByExtra;
    return visibleTabs.find((tab) => path.startsWith(tab.path));
  }, [location.pathname, visibleTabs]);

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [activeTab, visibleTabs, navigate]);

  useEffect(() => {
    document.title = `${t('shared.compliance', 'Compliance')} | Arcmedix`;
  }, [t]);

  if (!activeTab) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="alert alert-warning mt-4">
            {t('compliance.no_access', 'You do not have access to any Compliance modules.')}
          </div>
        </div>
      </div>
    );
  }

  const ActiveComponent = activeTab.component;

  return (
    <>
      <div className="page-content" style={{ paddingBottom: 0 }}>
        <div className="container-fluid">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <h4 className="mb-0 fw-bold">{t('shared.compliance', 'Compliance')}</h4>
          </motion.div>

          <Nav tabs className="compliance-tabs mb-4 border-bottom">
            {visibleTabs.map((tab) => (
              <NavItem key={tab.key} className="flex-shrink-0">
                <NavLink
                  className={classnames({ active: activeTab.key === tab.key })}
                  onClick={() => navigate(tab.path)}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon={tab.icon} size={16} className="me-2" />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
      </div>

      <div className="compliance-tab-content">
        <ActiveComponent />
      </div>

      <style>{`
        .compliance-tabs {
          gap: 0.5rem;
          flex-wrap: nowrap;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .compliance-tabs::-webkit-scrollbar { display: none; }
        .compliance-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
          white-space: nowrap;
        }
        .compliance-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
        .compliance-tab-content .page-content {
          padding-top: 0 !important;
        }
      `}</style>
    </>
  );
};

export default Compliance;
